<template>
  <component 
    :is="tag"
    :type="tag === 'button' ? nativeType : ''"
    @click="handleClick"
    :class="[
      'tw-inline-flex tw-items-center tw-justify-center tw-transition-colors tw-rounded-md tw-focus:outline-none tw-font-medium',
      classes
    ]"
  >
    <div :class="innerClass">
      <span v-if="$slots.icon || (icon && $slots.default)" class="tw-mr-2">
        <slot name="icon">
          <i :class="icon"></i>
        </slot>
      </span>
      <i v-if="!$slots.default" :class="icon"></i>
      <span v-if="$slots.icon || (icon && $slots.default)">
        <slot>{{ text }}</slot>
      </span>
      <slot v-if="!$slots.icon && !icon"></slot>
    </div>
  </component>
</template>

<script>
export default {
  name: "tw-base-button",
  props: {
    tag: {
      type: String,
      default: "button",
      description: "Button tag (default -> button)"
    },
    type: {
      type: String,
      default: "default",
      description: "Button type (e,g primary, danger etc)"
    },
    size: {
      type: String,
      default: "md",
      description: "Button size lg|md|sm"
    },
    innerClass: {
      type: String,
      default: "",
      description: "Custom classes for button content"
    },
    textColor: {
      type: String,
      default: "",
      description: "Button text color (e.g primary, danger etc)"
    },
    nativeType: {
      type: String,
      default: "button",
      description: "Button native type (e.g submit,button etc)"
    },
    icon: {
      type: String,
      default: "",
      description: "Button icon"
    },
    text: {
      type: String,
      default: "",
      description: "Button text in case not provided via default slot"
    },
    outline: {
      type: Boolean,
      default: false,
      description: "Whether button style is outline"
    },
    rounded: {
      type: Boolean,
      default: false,
      description: "Whether button is rounded"
    },
    iconOnly: {
      type: Boolean,
      default: false,
      description: "Whether button contains only an icon"
    },
    block: {
      type: Boolean,
      default: false,
      description: "Whether button is full width"
    },
    hidden: {
      type: Boolean,
      default: false,
      description: "Whether button is hidden"
    },
  },
  computed: {
    classes() {
      const typeClasses = {
        default: this.outline 
          ? 'tw-border-2 tw-border-gray-200 tw-hover:bg-gray-50 tw-text-gray-700'
          : 'tw-border-0 tw-bg-gray-200 tw-hover:bg-gray-300 tw-text-gray-700',
        primary: this.outline
          ? 'tw-border-2 tw-border-blue-500 tw-hover:bg-blue-50 tw-text-blue-500'
          : 'tw-border-0 tw-bg-blue-500 tw-hover:bg-blue-600 tw-text-white',
        danger: this.outline
          ? 'tw-border-2 tw-border-red-500 tw-hover:bg-red-50 tw-text-red-500'
          : 'tw-border-0 tw-bg-red-500 tw-hover:bg-red-600 tw-text-white',
        success: this.outline
          ? 'tw-border-2 tw-border-green-500 tw-hover:bg-green-50 tw-text-green-500'
          : 'tw-border-0 tw-bg-green-500 tw-hover:bg-green-600 tw-text-white',
        warning: this.outline
          ? 'tw-border-2 tw-border-amber-500 tw-hover:bg-amber-50 tw-text-amber-500'
          : 'tw-border-0 tw-bg-amber-500 tw-hover:bg-amber-600 tw-text-white',
      };

      const sizeClasses = {
        sm: this.iconOnly ? 'tw-p-1.5' : 'tw-px-3 tw-py-1.5 tw-text-sm',
        md: this.iconOnly ? 'tw-p-2' : 'tw-px-4 tw-py-2 tw-text-base',
        lg: this.iconOnly ? 'tw-p-3' : 'tw-px-6 tw-py-3 tw-text-lg',
      };

      return [
        typeClasses[this.type],
        sizeClasses[this.size],
        this.block && 'tw-w-full',
        this.rounded && 'tw-rounded-full',
        this.iconOnly && 'tw-aspect-square',
        this.textColor && `tw-text-${this.textColor}-500`,
        this.hidden && 'tw-hidden',
      ];
    }
  },
  methods: {
    handleClick(evt) {
      this.$emit("click", evt);
    }
  }
};
</script>